// src/pages/PlacementOverview.tsx
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaCalendarAlt,
  FaFileAlt,
  FaHourglassHalf,
  FaPeopleArrows,
  FaUsersCog,
} from "react-icons/fa";

import ErrorPage from "../components/ErrorPage";
import { useCan } from "../authz/useCan";
import {
  LdsOverviewCard,
  LdsOverviewEmpty,
  LdsOverviewGrid,
  LdsOverviewHero,
  LdsOverviewPage,
  LdsOverviewSection,
} from "../components/lds/LdsOverview";

export default function PlacementOverview() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const canViewPlacementOverview = useCan(
    "school.placement-overview",
    "view"
  );

  const canSeeApplications = [
    useCan("school.applications", "list"),
    useCan("school.applications", "view"),
    useCan("school.applications", "read"),
    useCan("school.applications", "crud"),
    useCan("school.applications", "manage"),
  ].some(Boolean);

  const canSeeWaiting = [
    useCan("school.waiting-list", "list"),
    useCan("school.waiting-list", "view"),
    useCan("school.waiting-list", "assign"),
  ].some(Boolean);

  const canSeeClassroomOverview = [
    useCan(
      "school.classrooms.overview",
      "manage"
    ),
    useCan("school.classrooms", "crud"),
    useCan("school.classrooms", "manage"),
  ].some(Boolean);

  const canViewFuturePlanning = [
    useCan("school.future-planning", "view"),
    useCan("school.future-planning", "manage"),
  ].some(Boolean);

  if (!canViewPlacementOverview) {
    return (
      <ErrorPage
        status={403}
        message={t("error.403.message", {
          defaultValue:
            "You do not have permission to view this page.",
        })}
      />
    );
  }

  const visibleSections = [
    canSeeApplications,
    canSeeWaiting,
    canViewFuturePlanning,
    canSeeClassroomOverview,
  ].filter(Boolean).length;

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaPeopleArrows}
        eyebrow={t(
          "placement.overview.eyebrow",
          { defaultValue: "School placement" }
        )}
        title={t("placement.overview.title", {
          defaultValue: "Placement",
        })}
        description={t(
          "placement.overview.description",
          {
            defaultValue:
              "Manage applications, interest lists, future planning and classroom placement.",
          }
        )}
      />

      {visibleSections > 0 ? (
        <LdsOverviewSection
          title={t(
            "placement.overview.sections.title",
            { defaultValue: "Placement sections" }
          )}
          description={t(
            "placement.overview.availableSections",
            {
              defaultValue:
                "{{count}} sections available",
              count: visibleSections,
            }
          )}
        >
          <LdsOverviewGrid>
            {canSeeApplications ? (
              <LdsOverviewCard
                icon={FaFileAlt}
                eyebrow={t(
                  "placement.overview.cards.applications.category",
                  { defaultValue: "Admissions" }
                )}
                title={t(
                  "placement.overview.applications",
                  { defaultValue: "Applications" }
                )}
                description={t(
                  "applications.description",
                  {
                    defaultValue:
                      "Review and process new applications.",
                  }
                )}
                actionLabel={t("common.open", {
                  defaultValue: "Open",
                })}
                onClick={() =>
                  navigate("/school/applications")
                }
                animationIndex={0}
              />
            ) : null}

            {canSeeWaiting ? (
              <LdsOverviewCard
                icon={FaHourglassHalf}
                eyebrow={t(
                  "placement.overview.cards.waiting.category",
                  { defaultValue: "Placement" }
                )}
                title={t(
                  "placement.overview.waitingList",
                  { defaultValue: "Interest list" }
                )}
                description={t(
                  "waiting-list.description",
                  {
                    defaultValue:
                      "View and place children from the interest list.",
                  }
                )}
                actionLabel={t("common.open", {
                  defaultValue: "Open",
                })}
                onClick={() =>
                  navigate("/school/waiting-list")
                }
                animationIndex={1}
              />
            ) : null}

            {canViewFuturePlanning ? (
              <LdsOverviewCard
                icon={FaCalendarAlt}
                eyebrow={t(
                  "placement.overview.cards.future.category",
                  { defaultValue: "Planning" }
                )}
                title={t(
                  "placement.overview.futurePlanning",
                  {
                    defaultValue:
                      "Future planning",
                  }
                )}
                description={t(
                  "placement.overview.futurePlanningDescription",
                  {
                    defaultValue:
                      "Review children expected to move to the next school year.",
                  }
                )}
                actionLabel={t("common.open", {
                  defaultValue: "Open",
                })}
                onClick={() =>
                  navigate(
                    "/plaatsing/toekomstige-planning"
                  )
                }
                animationIndex={2}
              />
            ) : null}

            {canSeeClassroomOverview ? (
              <LdsOverviewCard
                icon={FaUsersCog}
                eyebrow={t(
                  "placement.overview.cards.classrooms.category",
                  { defaultValue: "Classrooms" }
                )}
                title={t(
                  "placement.overview.classroomOverview",
                  {
                    defaultValue:
                      "Classroom overview",
                  }
                )}
                description={t(
                  "classrooms.overview_management.description",
                  {
                    defaultValue:
                      "Assign, move and plan children by school year.",
                  }
                )}
                actionLabel={t("common.open", {
                  defaultValue: "Open",
                })}
                onClick={() =>
                  navigate(
                    "/school/classrooms/overview"
                  )
                }
                animationIndex={3}
              />
            ) : null}
          </LdsOverviewGrid>
        </LdsOverviewSection>
      ) : (
        <LdsOverviewEmpty>
          {t("common.no_access", {
            defaultValue:
              "You do not have access to any items in this section.",
          })}
        </LdsOverviewEmpty>
      )}
    </LdsOverviewPage>
  );
}
